// src/components/PermissionGuard.js
import React from 'react';

export default function PermissionGuard({
  required,              // string | string[] (alias of requiredPermission)
  requiredPermission,    // string | string[]
  children,
  fallback = null,       // optional custom UI when blocked
  overlay = true,        // show full-screen overlay or inline card
  requireAll = false     // if array: true => ALL required, false => ANY
}) {
  const employee = safeGetEmployee();
  const isSuperAdmin = employee?.role === 'super_admin';
  const userPermissions = Array.isArray(employee?.permissions) ? employee.permissions : [];

  // normalize required permissions
  const needed = requiredPermission ?? required;
  const neededList = Array.isArray(needed) ? needed.filter(Boolean) : (needed ? [needed] : []);

  const hasPermission =
    isSuperAdmin ||
    (neededList.length === 0
      ? true
      : requireAll
        ? neededList.every(p => userPermissions.includes(p))
        : neededList.some(p => userPermissions.includes(p)));

  if (!hasPermission) {
    // Never render children when access is denied
    if (fallback) return fallback;
    return overlay ? <OverlayDenied /> : <InlineDenied />;
  }

  return <>{children}</>;
}

/* ---------- helpers ---------- */
function safeGetEmployee() {
  try {
    const raw = localStorage.getItem('employee');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/* ---------- default UIs ---------- */
function OverlayDenied() {
  return (
    <div style={styles.container}>
      <div style={styles.popup}>
        <h2 style={{ marginBottom: '0.5rem' }}>🚫 Access Denied</h2>
        <p style={{ margin: 0 }}>You do not have permission to view this content.</p>
      </div>
    </div>
  );
}

function InlineDenied() {
  return (
    <div style={styles.inlineWrap}>
      <div style={styles.inlineCard}>
        <strong>Access Denied</strong>
        <div style={{ color: '#666', marginTop: 4 }}>You lack the required permission.</div>
      </div>
    </div>
  );
}

/* ---------- styles ---------- */
const styles = {
  container: {
    position: 'fixed',
    top: 0, left: 0,
    width: '100vw', height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999
  },
  popup: {
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '400px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
    fontFamily: 'sans-serif',
    textAlign: 'center'
  },
  inlineWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: '2rem'
  },
  inlineCard: {
    background: '#fff',
    border: '1px solid #eee',
    borderRadius: 10,
    padding: '1rem 1.25rem',
    boxShadow: '0 2px 10px rgba(0,0,0,.06)',
    fontFamily: 'sans-serif'
  }
};
